/* ==========================================================================
   Zikros — theme scripts
   Vanilla JS + custom elements. No dependencies.
   ========================================================================== */

(function () {
  'use strict';

  const Theme = (window.Theme = window.Theme || {});
  Theme.routes = window.routes || {};
  Theme.strings = window.themeStrings || {};

  /* ---------------------------------------------------------------- utils */

  const on = (el, evt, fn, opts) => el && el.addEventListener(evt, fn, opts);
  const $ = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));

  const debounce = (fn, wait) => {
    let t;
    return function () {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, arguments), wait);
    };
  };

  const formatMoney = (cents) => {
    const format = Theme.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace(/\{\{\s*amount\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toLocaleString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, Math.round(cents / 100).toString());
  };
  Theme.formatMoney = formatMoney;

  const trapFocus = (container) => {
    const focusable = $$(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      container
    ).filter((el) => el.offsetParent !== null);
    if (!focusable.length) return () => {};
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const handler = (event) => {
      if (event.key !== 'Tab') return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', handler);
    window.setTimeout(() => first.focus(), 60);
    return () => container.removeEventListener('keydown', handler);
  };

  /* --------------------------------------------------------------- toasts */

  let toastTimer;
  Theme.toast = function (message) {
    let toast = $('.toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'toast';
      toast.setAttribute('role', 'status');
      document.body.appendChild(toast);
    }
    toast.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg><span></span>';
    toast.querySelector('span').textContent = message;
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 2600);
  };

  /* -------------------------------------------------------------- drawers */

  const Drawers = {
    active: null,
    releaseFocus: null,
    overlay() {
      let overlay = $('#drawer-overlay');
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'drawer-overlay';
        overlay.className = 'drawer-overlay';
        document.body.appendChild(overlay);
        on(overlay, 'click', () => Drawers.close());
      }
      return overlay;
    },
    open(id, opener) {
      const drawer = document.getElementById(id);
      if (!drawer) return;
      if (Drawers.active && Drawers.active !== drawer) Drawers.close(true);
      Drawers.active = drawer;
      Drawers.opener = opener || document.activeElement;
      drawer.classList.add('is-open');
      drawer.removeAttribute('aria-hidden');
      Drawers.overlay().classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      Drawers.releaseFocus = trapFocus(drawer);
      drawer.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    },
    close(skipOverlay) {
      const drawer = Drawers.active;
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      $$('.menu-drawer__panel.is-open', drawer).forEach((p) => p.classList.remove('is-open'));
      if (Drawers.releaseFocus) Drawers.releaseFocus();
      Drawers.releaseFocus = null;
      Drawers.active = null;
      if (!skipOverlay) {
        Drawers.overlay().classList.remove('is-open');
        document.body.classList.remove('overflow-hidden');
      }
      if (Drawers.opener && typeof Drawers.opener.focus === 'function') Drawers.opener.focus();
      drawer.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }
  };
  Theme.Drawers = Drawers;

  /*
    The header section is `position: sticky`, which always creates a stacking
    context. Drawers rendered inside it are therefore trapped below the
    body-level overlay, which then covers them and swallows every tap. Move
    them to the body so they share a stacking context with the overlay.
  */
  function relocateDrawers() {
    $$('[data-drawer-relocated]').forEach((node) => node.remove());
    ['menu-drawer', 'search-drawer'].forEach((id) => {
      const drawer = document.getElementById(id);
      if (!drawer || drawer.parentElement === document.body) return;
      drawer.setAttribute('data-drawer-relocated', 'true');
      document.body.appendChild(drawer);
    });
  }
  Theme.relocateDrawers = relocateDrawers;

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      const id = opener.getAttribute('data-drawer-open');
      if (document.getElementById(id)) {
        event.preventDefault();
        Drawers.open(id, opener);
      }
      return;
    }
    if (event.target.closest('[data-drawer-close]')) {
      event.preventDefault();
      Drawers.close();
      return;
    }
    const panelOpener = event.target.closest('[data-panel-open]');
    if (panelOpener) {
      event.preventDefault();
      const panel = document.getElementById(panelOpener.getAttribute('data-panel-open'));
      if (panel) panel.classList.add('is-open');
      return;
    }
    const panelCloser = event.target.closest('[data-panel-close]');
    if (panelCloser) {
      event.preventDefault();
      const panel = panelCloser.closest('.menu-drawer__panel');
      if (panel) panel.classList.remove('is-open');
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const modal = $('.modal.is-open');
    if (modal) {
      closeModal(modal);
      return;
    }
    if (Drawers.active) Drawers.close();
  });

  /* ------------------------------------------------------------ video modal */

  function closeModal(modal) {
    modal.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    const frame = $('iframe', modal);
    if (frame) frame.src = frame.src;
    const video = $('video', modal);
    if (video) video.pause();
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-modal-open]');
    if (trigger) {
      const modal = document.getElementById(trigger.getAttribute('data-modal-open'));
      if (!modal) return;
      event.preventDefault();
      modal.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      const video = $('video', modal);
      if (video) video.play().catch(() => {});
      return;
    }
    const closer = event.target.closest('[data-modal-close]');
    if (closer) {
      event.preventDefault();
      const modal = closer.closest('.modal');
      if (modal) closeModal(modal);
      return;
    }
    if (event.target.classList && event.target.classList.contains('modal')) closeModal(event.target);
  });

  /* ------------------------------------------------------- header dropdowns */

  function initHeaderMenus(scope) {
    $$('.nav__item--has-children', scope || document).forEach((item) => {
      if (item.dataset.menuBound === 'true') return;
      item.dataset.menuBound = 'true';
      const link = $('.nav__link', item);
      let timer;

      const open = () => {
        clearTimeout(timer);
        $$('.nav__item.is-open').forEach((other) => {
          if (other !== item) other.classList.remove('is-open');
        });
        item.classList.add('is-open');
        if (link) link.setAttribute('aria-expanded', 'true');
      };
      const close = () => {
        item.classList.remove('is-open');
        if (link) link.setAttribute('aria-expanded', 'false');
      };

      on(item, 'mouseenter', open);
      on(item, 'mouseleave', () => {
        timer = setTimeout(close, 120);
      });
      on(item, 'focusin', open);
      on(item, 'focusout', (event) => {
        if (!item.contains(event.relatedTarget)) close();
      });
      if (link) {
        on(link, 'click', (event) => {
          if (window.matchMedia('(hover: none)').matches) {
            event.preventDefault();
            item.classList.contains('is-open') ? close() : open();
          }
        });
      }
    });
  }

  /* -------------------------------------------------------- sticky header */

  function initStickyHeader() {
    const header = $('.header-wrapper--sticky');
    if (!header) return;
    const onScroll = () => header.classList.toggle('is-stuck', window.scrollY > 4);
    onScroll();
    on(window, 'scroll', onScroll, { passive: true });
  }

  /* ----------------------------------------------------------- animations */

  function initAnimations(scope) {
    const items = $$('.animate:not(.is-visible)', scope || document);
    if (!items.length) return;
    if (!('IntersectionObserver' in window) || document.body.dataset.animate === 'false') {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -40px 0px', threshold: 0.05 }
    );
    items.forEach((el) => observer.observe(el));
  }

  /* -------------------------------------------------------------- sliders */

  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.slider = this.querySelector('[data-slider]');
      this.prev = this.querySelector('[data-slider-prev]');
      this.next = this.querySelector('[data-slider-next]');
      this.dots = Array.from(this.querySelectorAll('[data-slider-dot]'));
      if (!this.slider) return;

      this.update = debounce(() => this.updateButtons(), 80);
      on(this.prev, 'click', () => this.scrollByPage(-1));
      on(this.next, 'click', () => this.scrollByPage(1));
      this.dots.forEach((dot, index) => on(dot, 'click', () => this.scrollToIndex(index)));
      on(this.slider, 'scroll', this.update, { passive: true });
      on(window, 'resize', this.update);
      this.updateButtons();
    }

    get step() {
      const first = this.slider.firstElementChild;
      if (!first) return this.slider.clientWidth;
      const style = window.getComputedStyle(this.slider);
      const gap = parseFloat(style.columnGap || style.gap) || 0;
      return first.getBoundingClientRect().width + gap;
    }

    scrollByPage(direction) {
      const perView = Math.max(1, Math.floor(this.slider.clientWidth / this.step));
      this.slider.scrollBy({ left: direction * this.step * perView, behavior: 'smooth' });
    }

    scrollToIndex(index) {
      this.slider.scrollTo({ left: this.step * index, behavior: 'smooth' });
    }

    updateButtons() {
      const max = this.slider.scrollWidth - this.slider.clientWidth - 2;
      const atStart = this.slider.scrollLeft <= 2;
      const atEnd = this.slider.scrollLeft >= max;
      if (this.prev) this.prev.disabled = atStart;
      if (this.next) this.next.disabled = atEnd;
      const hidden = max <= 2;
      this.classList.toggle('slider-wrapper--inactive', hidden);
      if (this.dots.length) {
        const index = Math.round(this.slider.scrollLeft / this.step);
        this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      }
    }
  }
  if (!customElements.get('slider-component')) customElements.define('slider-component', SliderComponent);

  /* ---------------------------------------------------- rotating announcements */

  class RotatingSlides extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-slide-dot]'));
      this.index = 0;
      if (this.slides.length < 2) {
        this.querySelectorAll('[data-slide-prev], [data-slide-next]').forEach((b) => (b.hidden = true));
        if (this.slides.length < 2 && this.dots.length) this.dots.forEach((d) => (d.hidden = true));
      }
      on(this.querySelector('[data-slide-prev]'), 'click', () => this.go(this.index - 1));
      on(this.querySelector('[data-slide-next]'), 'click', () => this.go(this.index + 1));
      this.dots.forEach((dot, i) => on(dot, 'click', () => this.go(i)));

      const interval = parseInt(this.dataset.autoplay || '0', 10);
      if (interval > 0 && this.slides.length > 1) {
        this.timer = setInterval(() => this.go(this.index + 1), interval);
        on(this, 'mouseenter', () => clearInterval(this.timer));
      }
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    go(next) {
      if (!this.slides.length) return;
      this.index = (next + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === this.index));
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this.index);
        dot.setAttribute('aria-current', i === this.index ? 'true' : 'false');
      });
    }
  }
  if (!customElements.get('rotating-slides')) customElements.define('rotating-slides', RotatingSlides);

  /* ------------------------------------------------------- quantity input */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.querySelectorAll('button').forEach((button) =>
        on(button, 'click', (event) => {
          event.preventDefault();
          const step = button.dataset.action === 'increase' ? 1 : -1;
          const min = parseInt(this.input.min || '0', 10);
          const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
          const value = Math.min(max, Math.max(min, (parseInt(this.input.value, 10) || 0) + step));
          if (value === parseInt(this.input.value, 10)) return;
          this.input.value = value;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        })
      );
    }
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  /* ------------------------------------------------------------ cart core */

  const Cart = {
    sectionsToRender() {
      return ['cart-drawer', 'main-cart'].filter((id) => document.getElementById('shopify-section-' + id));
    },

    renderSections(sections) {
      if (!sections) return;
      Object.keys(sections).forEach((id) => {
        const html = sections[id];
        if (!html) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const source = parsed.getElementById('shopify-section-' + id) || parsed.body.firstElementChild;
        const target = document.getElementById('shopify-section-' + id);
        if (!source || !target) return;

        // Keep any drawer inside this section open across the re-render.
        const openIds = $$('.drawer.is-open', target).map((drawer) => drawer.id);
        const activeId = Drawers.active ? Drawers.active.id : null;

        target.innerHTML = source.innerHTML;

        openIds.forEach((drawerId) => {
          const drawer = document.getElementById(drawerId);
          if (!drawer) return;
          drawer.classList.add('is-open');
          drawer.removeAttribute('aria-hidden');
          if (drawerId === activeId) Drawers.active = drawer;
        });
        initAll(target);
      });
    },

    updateCount(count) {
      if (typeof count !== 'number') return;
      document.documentElement.style.setProperty('--cart-count', count);
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = count === 0;
      });
      $$('[data-cart-count-label]').forEach((el) => {
        el.textContent = (Theme.strings.cartItemCount || '[[n]] items').replace('[[n]]', count);
      });
    },

    async request(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(Object.assign({ sections: Cart.sectionsToRender(), sections_url: window.location.pathname }, body))
      });
      const data = await response.json();
      if (!response.ok) throw data;
      return data;
    },

    async add(formData) {
      formData.append('sections', Cart.sectionsToRender().join(','));
      formData.append('sections_url', window.location.pathname);
      const response = await fetch(Theme.routes.cart_add_url || '/cart/add', {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      const data = await response.json();
      if (!response.ok || data.status) throw data;
      Cart.renderSections(data.sections);
      await Cart.refreshCount();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      return data;
    },

    async change(line, quantity) {
      const data = await Cart.request(Theme.routes.cart_change_url || '/cart/change', { line: line, quantity: quantity });
      Cart.renderSections(data.sections);
      Cart.updateCount(data.item_count);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      return data;
    },

    async updateNote(note) {
      return Cart.request(Theme.routes.cart_update_url || '/cart/update', { note: note });
    },

    async refreshCount() {
      try {
        const response = await fetch((Theme.routes.cart_url || '/cart') + '.js');
        const cart = await response.json();
        Cart.updateCount(cart.item_count);
        return cart;
      } catch (error) {
        return null;
      }
    },

    openDrawer() {
      if (document.body.dataset.cartType !== 'drawer') return false;
      if (!document.getElementById('cart-drawer')) return false;
      Drawers.open('cart-drawer');
      return true;
    }
  };
  Theme.Cart = Cart;

  /* ------------------------------------------------------- product form */

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.submitButton = this.querySelector('[data-add-to-cart]');
      this.errorTarget = this.querySelector('[data-form-error]');
      on(this.form, 'submit', (event) => this.onSubmit(event));
    }

    async onSubmit(event) {
      if (this.dataset.ajax === 'false') return;
      event.preventDefault();
      if (this.submitButton && this.submitButton.disabled) return;
      this.setLoading(true);
      if (this.errorTarget) this.errorTarget.hidden = true;

      try {
        await Cart.add(new FormData(this.form));
        Theme.toast(Theme.strings.addedToCart || 'Added to cart');
        if (!Cart.openDrawer() && document.body.dataset.cartType !== 'drawer') {
          window.location.href = Theme.routes.cart_url || '/cart';
          return;
        }
      } catch (error) {
        const message = (error && (error.description || error.message)) || 'Something went wrong. Please try again.';
        if (this.errorTarget) {
          this.errorTarget.textContent = message;
          this.errorTarget.hidden = false;
        } else {
          Theme.toast(message);
        }
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(state) {
      if (!this.submitButton) return;
      this.submitButton.classList.toggle('is-loading', state);
      this.submitButton.disabled = state || this.submitButton.dataset.soldOut === 'true';
      if (state && !this.submitButton.querySelector('.loading-spinner')) {
        const spinner = document.createElement('span');
        spinner.className = 'loading-spinner';
        this.submitButton.appendChild(spinner);
      }
      if (!state) {
        const spinner = this.submitButton.querySelector('.loading-spinner');
        if (spinner) spinner.remove();
      }
    }
  }
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  /* -------------------------------------------------------- cart elements */

  class CartItems extends HTMLElement {
    connectedCallback() {
      on(this, 'change', (event) => {
        const input = event.target.closest('[data-quantity-input]');
        if (!input) return;
        this.updateLine(input.dataset.line, parseInt(input.value, 10));
      });
      on(this, 'click', (event) => {
        const remove = event.target.closest('[data-remove-line]');
        if (!remove) return;
        event.preventDefault();
        this.updateLine(remove.dataset.line, 0);
      });
      const note = this.querySelector('[data-cart-note]');
      if (note) on(note, 'change', debounce(() => Cart.updateNote(note.value), 300));
    }

    async updateLine(line, quantity) {
      if (!line || isNaN(quantity)) return;
      this.classList.add('is-loading');
      try {
        await Cart.change(line, quantity);
      } catch (error) {
        Theme.toast('Could not update the cart. Please refresh and try again.');
      } finally {
        this.classList.remove('is-loading');
      }
    }
  }
  if (!customElements.get('cart-items')) customElements.define('cart-items', CartItems);

  /* ------------------------------------------------------ variant picker */

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.url = this.dataset.url;
      const data = this.querySelector('[data-variants-json]');
      this.variants = data ? JSON.parse(data.textContent) : [];
      on(this, 'change', () => this.onChange());
      this.onChange(true);
    }

    get selectedOptions() {
      return Array.from(this.querySelectorAll('input[type="radio"]:checked, select')).map((input) => input.value);
    }

    onChange(initial) {
      const selected = this.selectedOptions;
      const variant = this.variants.find((v) =>
        (v.options || []).every((option, index) => option === selected[index])
      );
      this.updateAvailability(selected);
      if (!variant) {
        this.setUnavailable();
        return;
      }
      if (!initial) this.updateUrl(variant);
      this.updateInputs(variant);
      this.updatePrice(variant);
      this.updateMedia(variant);
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: variant, section: this.sectionId } }));
    }

    updateAvailability(selected) {
      const inputs = Array.from(this.querySelectorAll('input[type="radio"]'));
      inputs.forEach((input) => {
        const position = parseInt(input.dataset.optionPosition, 10) - 1;
        const candidate = selected.slice();
        candidate[position] = input.value;
        const match = this.variants.find((v) =>
          (v.options || []).every((option, index) => (index === position ? option === input.value : option === candidate[index]))
        );
        const available = !!(match && match.available);
        input.disabled = false;
        input.nextElementSibling && input.nextElementSibling.classList.toggle('is-unavailable', !available);
      });
    }

    updateInputs(variant) {
      $$('[data-variant-id][data-section="' + this.sectionId + '"]').forEach((input) => {
        input.value = variant.id;
        input.disabled = !variant.available;
      });

      const button = document.querySelector('[data-add-to-cart][data-section="' + this.sectionId + '"]');
      if (button) {
        const text = button.querySelector('.btn__text') || button;
        button.disabled = !variant.available;
        button.dataset.soldOut = variant.available ? 'false' : 'true';
        text.textContent = variant.available
          ? Theme.strings.addToCart || 'Add to Cart'
          : Theme.strings.soldOut || 'Sold out';
      }
      const paymentButton = document.querySelector('[data-payment-button][data-section="' + this.sectionId + '"]');
      if (paymentButton) paymentButton.style.display = variant.available ? '' : 'none';

      const sku = document.querySelector('[data-variant-sku][data-section="' + this.sectionId + '"]');
      if (sku) sku.textContent = variant.sku || '';

      $$('[data-option-selected]', this).forEach((label) => {
        const position = parseInt(label.dataset.optionSelected, 10) - 1;
        label.textContent = (variant.options || [])[position] || '';
      });
    }

    setUnavailable() {
      const button = document.querySelector('[data-add-to-cart][data-section="' + this.sectionId + '"]');
      if (button) {
        const text = button.querySelector('.btn__text') || button;
        button.disabled = true;
        button.dataset.soldOut = 'true';
        text.textContent = Theme.strings.unavailable || 'Unavailable';
      }
    }

    updateUrl(variant) {
      if (!this.url || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', this.url + '?variant=' + variant.id);
    }

    updatePrice(variant) {
      const container = document.querySelector('[data-price-wrapper][data-section="' + this.sectionId + '"]');
      if (!container) return;
      const regular = container.querySelector('[data-price-regular]');
      const compare = container.querySelector('[data-price-compare]');
      const saving = container.querySelector('[data-price-saving]');
      if (regular) regular.textContent = formatMoney(variant.price);
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (compare) {
        compare.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
        compare.hidden = !onSale;
      }
      if (saving) {
        if (onSale) {
          const percent = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          saving.textContent = (Theme.strings.savePercent || 'Save [[n]]%').replace('[[n]]', percent);
          saving.hidden = false;
        } else {
          saving.hidden = true;
        }
      }
      container.classList.toggle('price--on-sale', !!onSale);
    }

    updateMedia(variant) {
      if (!variant.featured_media) return;
      const gallery = document.querySelector('product-gallery[data-section="' + this.sectionId + '"]');
      if (gallery && typeof gallery.showMediaById === 'function') gallery.showMediaById(variant.featured_media.id);
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  /* ------------------------------------------------------ product gallery */

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-media-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-media-thumb]'));
      this.thumbs.forEach((thumb) =>
        on(thumb, 'click', (event) => {
          event.preventDefault();
          this.show(thumb.dataset.mediaThumb);
        })
      );
      const play = this.querySelector('[data-gallery-play]');
      if (play) on(play, 'click', () => this.playActiveVideo());
    }

    show(mediaId) {
      this.slides.forEach((slide) => {
        const isActive = slide.dataset.mediaSlide === String(mediaId);
        slide.classList.toggle('is-active', isActive);
        if (!isActive) {
          const video = slide.querySelector('video');
          if (video) video.pause();
        }
      });
      this.thumbs.forEach((thumb) =>
        thumb.setAttribute('aria-current', thumb.dataset.mediaThumb === String(mediaId) ? 'true' : 'false')
      );
      const play = this.querySelector('[data-gallery-play]');
      if (play) {
        const active = this.slides.find((slide) => slide.classList.contains('is-active'));
        play.hidden = !active || active.dataset.mediaType !== 'video';
      }
    }

    showMediaById(mediaId) {
      if (this.slides.some((slide) => slide.dataset.mediaSlide === String(mediaId))) this.show(mediaId);
    }

    playActiveVideo() {
      const active = this.slides.find((slide) => slide.classList.contains('is-active'));
      if (!active) return;
      const video = active.querySelector('video');
      const iframe = active.querySelector('iframe');
      const play = this.querySelector('[data-gallery-play]');
      if (video) {
        video.controls = true;
        video.play().catch(() => {});
      } else if (iframe && iframe.dataset.src) {
        iframe.src = iframe.dataset.src;
      }
      if (play) play.hidden = true;
    }
  }
  if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);

  /* ------------------------------------------------- expandable content */

  class ExpandableContent extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('[data-expandable-content]');
      this.button = this.querySelector('[data-expandable-toggle]');
      this.label = this.querySelector('[data-expandable-label]');
      if (!this.content || !this.button) return;

      this.maxHeight = parseInt(this.dataset.maxHeight || '480', 10);
      this.expanded = false;
      on(this.button, 'click', () => this.toggle());

      this.measure();

      // Imported descriptions are mostly images: their height only settles
      // once they load, so keep measuring until it does.
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(() => {
          if (!this.expanded) this.measure();
        });
        this.observer.observe(this.content);
      } else {
        on(window, 'load', () => this.measure());
      }
      on(window, 'resize', debounce(() => { if (!this.expanded) this.measure(); }, 200));
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    measure() {
      const overflows = this.content.scrollHeight > this.maxHeight + 60;
      this.classList.toggle('is-collapsible', overflows);
      this.button.hidden = !overflows;
      this.content.style.maxHeight = overflows ? this.maxHeight + 'px' : '';
    }

    toggle() {
      this.expanded = !this.expanded;
      this.classList.toggle('is-expanded', this.expanded);
      this.content.style.maxHeight = this.expanded ? '' : this.maxHeight + 'px';
      this.button.setAttribute('aria-expanded', String(this.expanded));
      if (this.label) {
        this.label.textContent = this.expanded
          ? Theme.strings.readLess || 'Read less'
          : Theme.strings.readMore || 'Read more';
      }
      if (!this.expanded) {
        const top = this.getBoundingClientRect().top + window.scrollY - 120;
        window.scrollTo({ top: top, behavior: 'smooth' });
      }
    }
  }
  if (!customElements.get('expandable-content')) customElements.define('expandable-content', ExpandableContent);

  /* ----------------------------------------------------- order tracking */

  class OrderTracker extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-tracking-input]');
      this.button = this.querySelector('[data-tracking-submit]');
      this.error = this.querySelector('[data-tracking-error]');
      if (!this.input || !this.button) return;

      on(this.button, 'click', () => this.track());
      on(this.input, 'keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.track();
        }
      });
      on(this.input, 'input', () => {
        if (this.error) this.error.hidden = true;
      });
    }

    track() {
      const number = (this.input.value || '').trim();
      if (!number) {
        if (this.error) this.error.hidden = false;
        this.input.focus();
        return;
      }
      const template = this.dataset.url || '';
      if (!template.includes('[number]')) return;
      const url = template.replace('[number]', encodeURIComponent(number));
      window.open(url, '_blank', 'noopener');
    }
  }
  if (!customElements.get('order-tracker')) customElements.define('order-tracker', OrderTracker);

  /* ---------------------------------------------------- predictive search */

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      if (!this.input || !this.results || this.dataset.enabled === 'false') return;
      on(this.input, 'input', debounce(() => this.search(), 260));
      on(this.closest('.search-drawer') || this, 'drawer:open', () => this.input.focus());
    }

    async search() {
      const term = this.input.value.trim();
      if (term.length < 2) {
        this.results.innerHTML = '';
        return;
      }
      try {
        const url =
          (Theme.routes.predictive_search_url || '/search/suggest') +
          '?q=' + encodeURIComponent(term) +
          '&resources[type]=product,collection,page&resources[limit]=6&section_id=predictive-search';
        const response = await fetch(url);
        const text = await response.text();
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const content = parsed.querySelector('[data-predictive-content]');
        this.results.innerHTML = content ? content.innerHTML : '';
      } catch (error) {
        this.results.innerHTML = '';
      }
    }
  }
  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);

  /* ----------------------------------------------------------- facets/sort */

  function initFacets(scope) {
    $$('[data-facet-form]', scope || document).forEach((form) => {
      if (form.dataset.bound === 'true') return;
      form.dataset.bound = 'true';
      on(form, 'change', () => {
        const params = new URLSearchParams(new FormData(form)).toString();
        window.location.search = params;
      });
    });
  }

  /* ------------------------------------------------------------- init all */

  function initAll(scope) {
    initHeaderMenus(scope);
    initAnimations(scope);
    initFacets(scope);
  }

  function init() {
    relocateDrawers();
    initAll(document);
    initStickyHeader();
    Cart.refreshCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* --------------------------------------------- Shopify theme editor hooks */

  document.addEventListener('shopify:section:load', (event) => {
    relocateDrawers();
    initAll(event.target);
  });
  document.addEventListener('shopify:section:select', (event) => {
    const drawer = event.target.querySelector('.drawer');
    if (drawer) Drawers.open(drawer.id);
  });
  document.addEventListener('shopify:section:deselect', () => {
    if (Drawers.active) Drawers.close();
  });
  document.addEventListener('shopify:block:select', (event) => {
    const slider = event.target.closest('slider-component');
    if (slider && event.target.parentElement) {
      const index = Array.from(event.target.parentElement.children).indexOf(event.target);
      if (typeof slider.scrollToIndex === 'function') slider.scrollToIndex(index);
    }
    const rotating = event.target.closest('rotating-slides');
    if (rotating && event.target.parentElement) {
      const index = Array.from(event.target.parentElement.children).indexOf(event.target);
      if (typeof rotating.go === 'function') rotating.go(index);
    }
  });
})();
